import { useEffect, useState, useRef } from 'react';
import { Pencil, Trash2, Plus, Upload, Eye } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { setShowTour, themeStore } from '../../../stores/theme';
import { useNavigate } from 'react-router-dom';
import { useStore } from '@tanstack/react-store';
import { useListEventsQuery, useAddNewEvent, useChangeEventStatus, useUpdateEvent, useDeleteEvent } from "../../../hooks/useEvents"
import { useSubscriptionPackagesListQuery } from "../../../hooks/useSubscriptions"
import { useUser } from '../../../hooks/useAuth';
import { noData } from '../../ui/media';
import { useSingleSubmit } from '../../../hooks/useSingleSubmit';
import { Tour } from 'antd';
import eventIcon from "../../../assets/icons/calendar.png"
import DatePicker from 'react-datepicker';
import toast from 'react-hot-toast';
import AddressAutocomplete from '../../common/AddressAutoComplete';
import Modal from '../../common/ModalComponent';
import SingleSubmitForm from '../../common/SingleSubmitForm';
import 'react-datepicker/dist/react-datepicker.css';
import moment from 'moment/moment';

const EventsPage = () => {
    const { isDarkMode, isShowTour } = useStore(themeStore)
    const AddEventRef = useRef(null);
    const UpgradeEventRef = useRef(null);
    const StatusEventRef = useRef(null);
    const EditEventRef = useRef(null);
    const DeleteEventRef = useRef(null);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const [currentEvent, setCurrentEvent] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [eventImage, setEventImage] = useState(null);
    const [eventList, setEventList] = useState(null);
    const [name, setName] = useState("");
    const [productId, setProductId] = useState("");
    const [date, setDate] = useState("");
    const [location, setLocation] = useState({
        coordinates: [],
        type: "point",
        name: ""
    });
    const [userData, setUserData] = useState("");
    const [subscriptionPackagesList, setSubscriptionPackagesList] = useState("");
    const { data: eventResponse, isSuccess } = useListEventsQuery()
    const { data: userDataResponse, isSuccess: userIsSuccess } = useUser()
    const { canSubmit, resetSubmit } = useSingleSubmit();
    const changeStatusMutation = useChangeEventStatus();
    const addNewEventMutation = useAddNewEvent()
    const updateEventMutation = useUpdateEvent()
    const deleteEventMutation = useDeleteEvent()
    const queryClient = useQueryClient();
    const navigate = useNavigate()
    const { data: subscriptionPackagesResponse, isSuccess: subscriptionPackagesSuccess } = useSubscriptionPackagesListQuery(
        userIsSuccess && userDataResponse?.data?.data?.user_type
            ? `?product_type=${userDataResponse?.data?.data?.user_type == 2 ? "event" : "security"}`
            : null,
        {
            enabled: !!(userIsSuccess && userDataResponse?.data?.data?.user_type),
        }
    );
    // upgrade Plan
    const [selectedPlanId, setSelectedPlanId] = useState('');
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [priceDifference, setPriceDifference] = useState(0);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [currentPlan, setCurrentPlan] = useState(null);
    const [tourOpen, setTourOpen] = useState(true);
    const [showTourCheckbox, setShowTourCheckbox] = useState(false);
    const [steps, setSteps] = useState([]);

    const colors = {
        background: isDarkMode ? '#121212' : '#ffffff',
        card: isDarkMode ? '#1e1e1e' : '#f5f7fb',
        text: isDarkMode ? '#e0e0e0' : '#333333',
        secondaryText: isDarkMode ? '#a0a0a0' : '#666666',
        border: isDarkMode ? '#333333' : '#e0e0e0',
        primary: '#2563eb',
        accent: isDarkMode ? '#3b82f6' : '#2563eb',
        footer: isDarkMode ? '#1a1a1a' : '#f0f4f8',
        footerText: isDarkMode ? '#a0a0a0' : '#555555',
        modalBackground: isDarkMode ? '#1a1a1a' : '#ffffff',
        modalHeader: isDarkMode ? '#1e293b' : '#e5edff',
        inputBackground: isDarkMode ? '#2d2d2d' : '#ffffff'
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setEventImage(file); // store file
            const imageURL = URL.createObjectURL(file);
            setImagePreview(imageURL);
        }
    };

    const resetStates = () => {
        setName('');
        setProductId('');
        setDate('');
        setLocation('');
        setEventImage(null);
        setImagePreview('');
    }

    // Handle add event
    const handleAddEvent = () => {
        if (!canSubmit()) return;
        if (name == "") {
            toast.error("Event Name is not allowed to empty")
            resetSubmit();
            return
        }
        if (productId == "") {
            toast.error("Product Id is not allowed to empty")
            resetSubmit();
            return
        }
        if (date == "") {
            toast.error("Event Date is not allowed to empty")
            resetSubmit();
            return
        }
        if (location?.name == "") {
            toast.error("Event Location is not allowed to empty")
            resetSubmit();
            return
        }
        const eventFormData = new FormData();
        eventFormData.append('company_id', userData?._id)
        eventFormData.append("name", name);
        eventFormData.append("product_id", productId);
        eventFormData.append("date", date);
        eventFormData.append("location", JSON.stringify(location));
        if (eventImage) {
            eventFormData.append('event_media', eventImage);
        }
        addNewEventMutation.mutate(eventFormData, {
            onSuccess: (data) => {
                resetSubmit();
                setShowAddModal(false);
                resetStates()
                toast.success("New Event Added Successfully")
                queryClient.invalidateQueries({ queryKey: ['events'] });
                if (productId !== "free_trial") {
                    navigate(`/make-payment/${data?.data?.data?._id}/${userData?._id}/internal/event/purchase/${productId}`)
                }
            },
            onError: (err) => {
                resetSubmit();
                const message = err.response?.data?.message || "Add New Event Action Failed"
                toast.error(message)
            }
        })
    };

    // Toggle status
    const toggleStatus = (event_id, currentStatus) => {
        changeStatusMutation.mutate({ status: currentStatus === 1 ? 0 : 1, event_id },
            {
                onSuccess: () => {
                    toast.success("Event status updated successfully");
                    queryClient.invalidateQueries(['events']); // Adjust key if needed
                },
                onError: (err) => {
                    const message = err.response?.data?.message || "Failed to update event status";
                    toast.error(message);
                },
            });
    };

    // Open edit modal
    const handleEditClick = (event) => {
        setCurrentEvent(event);
        setImagePreview(event?.media ? `${process.env.REACT_APP_IMAGE_URL}/${event?.media}` : "")
        setName(event?.name)
        setDate(event?.date)
        setLocation(event?.location)
        setShowEditModal(true);
    };

    // Open delete modal
    const handleDeleteClick = (event) => {
        setCurrentEvent(event);
        setShowDeleteModal(true);
    };

    // Handle update event
    const handleUpdateEvent = () => {
        if (name == "") {
            toast.error("Event Name is not allowed to empty")
            return
        }
        if (date == "") {
            toast.error("Event Date is not allowed to empty")
            return
        }
        if (location?.name == "") {
            toast.error("Event Location is not allowed to empty")
            return
        }
        const eventFormData = new FormData();
        eventFormData.append('event_id', currentEvent?._id)
        eventFormData.append("name", name);
        eventFormData.append("date", date);
        eventFormData.append("location", JSON.stringify(location));
        if (eventImage) {
            eventFormData.append('event_media', eventImage);
        }
        updateEventMutation.mutate(eventFormData, {
            onSuccess: () => {
                toast.success("Event Updated Successfully")
                queryClient.invalidateQueries({ queryKey: ['events'] });
                queryClient.invalidateQueries({ queryKey: ['subscriptionPackagesList'] });
                setShowAddModal(false);
                resetStates()
            },
            onError: (err) => {
                const message = err.response?.data?.message || "Update Event Action Failed"
                toast.error(message)
            }
        })
        setShowEditModal(false);
    };

    // Handle delete event
    const handleDeleteEvent = () => {
        deleteEventMutation.mutate({ event_id: currentEvent._id }, {
            onSuccess: () => {
                toast.success("Event deleted successfully");
                queryClient.invalidateQueries(['events']); // Adjust key if needed
            },
            onError: (err) => {
                const message = err.response?.data?.message || "Failed to delete event";
                toast.error(message);
            },
        });
        setShowDeleteModal(false);
    };

    const handleUpgrade = (event) => {
        setSelectedEvent(event)
        setCurrentPlan(subscriptionPackagesList?.find((packageObj) => packageObj?.id == event?.product_id ?? null))
        setShowUpgradeModal(true)
    }

    const finishTour = () => {
        if (showTourCheckbox) {
            setShowTour(!showTourCheckbox)
        }
    }

    useEffect(() => {
        if (isSuccess) {
            setEventList(eventResponse?.data?.data?.result)
        }
    }, [isSuccess, eventResponse])

    useEffect(() => {
        if (subscriptionPackagesSuccess) {
            setSubscriptionPackagesList(subscriptionPackagesResponse?.data?.data)
        }
    }, [subscriptionPackagesSuccess, subscriptionPackagesResponse])

    useEffect(() => {
        setUserData(userDataResponse?.data?.data)
    }, userDataResponse)

    useEffect(() => {
        const plan = (subscriptionPackagesList && subscriptionPackagesList?.find(p => p.id === selectedPlanId)) ?? null;
        setSelectedPlan(plan);
        if (plan) {
            const diff = plan?.price_value - (currentPlan?.price_value ?? 0);
            setPriceDifference(diff > 0 ? diff : 0); // Only pay extra, no refunds
        } else {
            setPriceDifference(0);
        }
    }, [selectedPlanId, currentPlan, subscriptionPackagesList]);

    useEffect(() => {
        let temp = [{
            title: 'Create Event',
            description: 'Click here to create a new event',
            target: () => AddEventRef?.current,
        }]
        if (eventList?.length > 0) {
            temp.push({
                title: 'Change Event Status',
                description: 'Enable / Disable your event for limited time',
                target: () => StatusEventRef?.current,
            }, {
                title: 'Edit Event Details',
                description: 'Update your event details',
                target: () => EditEventRef?.current,
            }, {
                title: 'Delete Event',
                description: 'Delete the event permanently from the system',
                target: () => DeleteEventRef?.current,
            })
        }
        if (eventList?.filter((e) => (e?.payment_status == 2 || e.payment_status == 5))?.length > 0) {
            temp.push({
                title: 'Upgrade Event',
                description: 'Upgrade the user limit to higher scale',
                target: () => UpgradeEventRef?.current,
            })
        }
        setSteps(temp)
    }, [eventList])

    return (
        <>
            <div className={`w-full min-h-screen flex flex-col ${isDarkMode ? "bg-transparent" : "bg-white"}`} >
                {/* Main Content */}
                <main className="flex-grow py-8 px-6">
                    <div className="container mx-auto">
                        {/* Events Header */}
                        <div className="flex justify-between items-center mb-8">
                            <h1 className="text-3xl font-bold text-center w-full" style={{ color: colors.text }}>EVENTS</h1>
                            <button
                                ref={AddEventRef}
                                onClick={() => { queryClient.invalidateQueries({ queryKey: ['subscriptionPackagesList'] }); setShowAddModal(true) }}
                                className={` justify-center py-2 w-[200px] rounded-full border text-center ${isDarkMode ? "border-white text-white" : "border-blue-600 text-blue-600"} flex items-center`}>
                                <Plus size={18} className="mr-1" />
                                Add Event
                            </button>
                        </div>
                        {/* Events Table */}
                        <div className="overflow-x-auto bg-transparent">
                            <table className="min-w-full table-auto border-separate border-spacing-y-3">
                                <thead>
                                    <tr className={`rounded-lg ${isDarkMode ? "bg-[rgba(20,35,58,0.8)]" : "bg-blue-100"}`}>
                                        <th className={`px-4 py-4 text-left font-semibold text-sm ${isDarkMode ? "text-white" : "text-gray-800"}`}>Image</th>
                                        <th className={`px-4 py-4 text-left font-semibold text-sm ${isDarkMode ? "text-white" : "text-gray-800"}`}>Name of Event</th>
                                        <th className={`px-4 py-4 text-left font-semibold text-sm ${isDarkMode ? "text-white" : "text-gray-800"}`}>Access Code</th>
                                        <th className={`px-4 py-4 text-left font-semibold text-sm ${isDarkMode ? "text-white" : "text-gray-800"}`}>Date</th>
                                        <th className={`px-4 py-4 text-left font-semibold text-sm ${isDarkMode ? "text-white" : "text-gray-800"}`}>Event Location</th>
                                        <th className={`px-4 py-4 text-left font-semibold text-sm ${isDarkMode ? "text-white" : "text-gray-800"}`}>No. of Users</th>
                                        <th className={`px-4 py-4 text-left font-semibold text-sm ${isDarkMode ? "text-white" : "text-gray-800"}`}>Payment Status</th>
                                        <th className={`px-4 py-4 text-left font-semibold text-sm ${isDarkMode ? "text-white" : "text-gray-800"}`}>Status</th>
                                        <th className={`px-4 py-4 text-left font-semibold text-sm ${isDarkMode ? "text-white" : "text-gray-800"}`}>Action</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y">
                                    {eventList?.length > 0 ?
                                        eventList?.map((event) => (
                                            <tr key={event.id} className={`rounded-lg ${isDarkMode ? "bg-[rgba(20,35,58,0.8)]" : "bg-blue-100"}`}>
                                                <td className="px-4 py-4">
                                                    <img src={event?.media ? `${process.env.REACT_APP_IMAGE_URL}/${event?.media}` : eventIcon} alt={event?.name} className="h-10" />
                                                </td>
                                                <td className="px-4 py-4 font-medium" style={{ color: colors.text }}>{event?.name}</td>
                                                <td className="px-4 py-4" style={{ color: colors.secondaryText }}>{
                                                    (event?.payment_status == 2 || event?.payment_status == 5) ? event?.access_code : "******"
                                                }</td>
                                                <td className="px-4 py-4" style={{ color: colors.text }}>{event?.date}</td>
                                                <td className="px-4 py-4" style={{ color: colors.text }}>{event?.location?.name}</td>
                                                <td className="px-4 py-4 text-left" style={{ color: colors.text }}>{event?.user_limit}</td>
                                                <td className="px-4 py-4 text-left" style={{ color: colors.text }}>{
                                                    event?.payment_status == 1 ? "Pending" : event?.payment_status == 2 ? "Paid" : event?.payment_status == 5 ? "Free Trial" : "Rejected"
                                                }</td>
                                                <td className="px-4 py-4">
                                                    <button
                                                        ref={StatusEventRef}
                                                        disabled={changeStatusMutation?.isLoading}
                                                        onClick={() => toggleStatus(event?._id, event?.status)}
                                                        className="focus:outline-none">
                                                        <div className={`w-12 h-6 rounded-full flex items-center ${event?.status ? 'bg-green-500' : 'bg-gray-300'} p-1 transition-colors duration-200`}>
                                                            <div className={`w-4 h-4 rounded-full bg-white transform transition-transform duration-200 ${event.status ? 'translate-x-6' : 'translate-x-0'}`}></div>
                                                        </div>
                                                    </button>
                                                </td>
                                                <td className="px-4 py-7 flex space-x-4">
                                                    {event?.payment_status == 1 ?
                                                        <button
                                                            onClick={() => navigate(`/make-payment/${event?._id}/${userData?._id}/internal/event/purchase/${event?.product_id}`)}
                                                            className=" h-8 border rounded-full bg-blue-900 text-white w-[100px]">
                                                            Pay Now
                                                        </button>
                                                        :
                                                        <button
                                                            onClick={() => handleUpgrade(event)}
                                                            ref={UpgradeEventRef}
                                                            className=" h-8 border rounded-full bg-blue-900 text-white w-[100px]">
                                                            Upgrade
                                                        </button>
                                                    }
                                                    <button
                                                        onClick={() => navigate(`/events/details/${event?._id}`)}
                                                        className="text-blue-600 hover:text-blue-800">
                                                        <Eye size={20} />
                                                    </button>
                                                    <button
                                                        ref={EditEventRef}
                                                        onClick={() => handleEditClick(event)}
                                                        className="text-blue-600 hover:text-blue-800">
                                                        <Pencil size={20} />
                                                    </button>
                                                    <button
                                                        ref={DeleteEventRef}
                                                        onClick={() => handleDeleteClick(event)}
                                                        className="text-red-600 hover:text-red-800">
                                                        <Trash2 size={20} />
                                                    </button>
                                                </td>
                                            </tr>
                                        )) :
                                        <tr>
                                            <td colSpan={9}>
                                                <div className={` mx-auto w-[50%] py-16 ${isDarkMode ? "bg-[rgba(0,0,0,0.5)]" : "bg-[rgba(255,255,255,1)]"}`}>
                                                    <div className="flex justify-center items-center pt-8 mb-8">
                                                        <img
                                                            className='w-[250px]'
                                                            src={noData} />
                                                    </div>
                                                    <p className={`text-2xl ${isDarkMode ? "text-white" : "text-gray-800"} text-center`}>NO EVENTS YET</p>
                                                    <div className='w-full flex items-center justify-center py-3'>
                                                        <button
                                                            onClick={() => setShowAddModal(true)}
                                                            className={` justify-center py-2 w-[200px] rounded-full border text-center ${isDarkMode ? "border-white text-white" : "border-blue-600 text-blue-600"} flex items-center mt-8`}>
                                                            <Plus size={18} className="mr-1" />
                                                            Add Event
                                                        </button>
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </main>
                {/* Add Event Modal */}
                <Modal isDarkMode={isDarkMode} key={"addform"} title="ADD NEW EVENT" isOpen={showAddModal} onClose={() => setShowAddModal(false)}>
                    <SingleSubmitForm onSubmit={handleAddEvent}>
                        <p className={`text-sm text-center mb-1 ${isDarkMode ? "text-white" : "text-gray-800"}`}>Upload image</p>
                        <div className="mb-6 flex justify-center">
                            <div className="w-20 h-20 border-2 border-dashed border-blue-500 rounded-lg flex flex-col items-center justify-center cursor-pointer">
                                {imagePreview ? (
                                    <img src={imagePreview} alt="Preview" className="w-full h-full object-cover rounded-md" />
                                ) : (
                                    <Upload size={24} className="text-blue-600" />
                                )}
                                <input
                                    type='file'
                                    style={{ position: 'absolute', height: "90px", width: '90px', opacity: 0, cursor: 'pointer' }}
                                    accept='image/*'
                                    onChange={handleImageChange}
                                />
                            </div>
                        </div>
                        <div className="mb-4">
                            <label className="block text-sm font-medium mb-2" style={{ color: colors.text }}>Name of Event</label>
                            <input
                                type="text"
                                value={name}
                                placeholder="Name of Event"
                                className="w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500"
                                onChange={(e) => setName(e.target.value)}
                                style={{
                                    backgroundColor: colors.inputBackground,
                                    borderColor: colors.border,
                                    color: colors.text
                                }}
                            />
                        </div>
                        <div className="mb-4">
                            <label className="block text-sm font-medium mb-2" style={{ color: colors.text }}>Select Plan</label>
                            <select
                                value={productId}
                                className="w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500"
                                onChange={(e) => { setProductId(e.target.value); setDate("") }}
                                style={{
                                    backgroundColor: colors.inputBackground,
                                    borderColor: colors.border,
                                    color: colors.text
                                }}>
                                <option selected={true} value={""}>Select Plan</option>
                                {userData?.first_entry &&
                                    <option selected={true} value={"free_trial"}>5 Users | Free Trial</option>
                                }
                                {subscriptionPackagesList && subscriptionPackagesList?.map((packageObj) => (
                                    <option value={packageObj?.id}>{`${packageObj?.description} | ($${packageObj?.price_value})`}</option>
                                ))}
                            </select>
                        </div>
                        <div className="mb-4">
                            <label className="block text-sm font-medium mb-2" style={{ color: colors.text }}>Date</label>
                            <DatePicker
                                selected={date ? new Date(date) : null}
                                onChange={(date) => setDate(date.toDateString() || "")}
                                maxDate={productId == "free_trial" ? moment().add("1", "month").toLocaleString() : null}
                                placeholderText="Select Date"
                                className="w-[465px] px-4 py-3 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500"
                                style={{
                                    backgroundColor: colors.inputBackground,
                                    borderColor: colors.border,
                                    color: colors.text
                                }}
                                minDate={new Date()}
                                dateFormat="dd-MM-yyyy"
                            />
                        </div>
                        <div className="mb-4">
                            <label className="block text-sm font-medium mb-2" style={{ color: colors.text }}>Event Address</label>
                            <AddressAutocomplete
                                placeholder={"Enter Event Address"}
                                value={location}
                                onSelect={(val) => setLocation(val)}
                            />
                        </div>
                        <div className='text-center'>
                            <button
                                type='submit'
                                disabled={addNewEventMutation?.isLoading ? true : false}
                                className="w-[200px] py-3 bg-blue-600 text-white rounded-full font-medium hover:bg-blue-700 transition mt-4">
                                {addNewEventMutation?.isLoading ? "Processing..." : productId == "free_trial" ? "Create Event" : "Pay Now"}
                            </button>
                        </div>
                    </SingleSubmitForm>
                </Modal>
                {/* Edit Event Modal */}
                <Modal isDarkMode={isDarkMode} key={"editform"} title="EDIT EVENT" isOpen={showEditModal} onClose={() => setShowEditModal(false)}>
                    <form
                        onSubmit={(e) => {
                            e.preventDefault()
                            handleUpdateEvent()
                        }}>
                        <p className={`text-sm text-center mb-1 ${isDarkMode ? "text-white" : "text-gray-800"}`}>Upload image</p>
                        <div className="mb-6 flex justify-center">
                            <div className="w-20 h-20 border-2 border-dashed border-blue-500 rounded-lg flex flex-col items-center justify-center cursor-pointer">
                                {imagePreview ? (
                                    <img src={imagePreview} alt="Preview" className="w-full h-full object-cover rounded-md" />
                                ) : (
                                    <Upload size={24} className="text-blue-600" />
                                )}
                                <input
                                    type='file'
                                    style={{ position: 'absolute', height: "90px", width: '90px', opacity: 0, cursor: 'pointer' }}
                                    accept='image/*'
                                    onChange={handleImageChange}
                                />
                            </div>
                        </div>
                        <div className="mb-4">
                            <label className="block text-sm font-medium mb-2" style={{ color: colors.text }}>Name of Event</label>
                            <input
                                type="text"
                                value={name}
                                placeholder="Name of Event"
                                className="w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500"
                                onChange={(e) => setName(e.target.value)}
                                style={{
                                    backgroundColor: colors.inputBackground,
                                    borderColor: colors.border,
                                    color: colors.text
                                }}
                            />
                        </div>
                        <div className="mb-4">
                            <label className="block text-sm font-medium mb-2" style={{ color: colors.text }}>Date</label>
                            <DatePicker
                                selected={date ? new Date(date) : null}
                                onChange={(date) => setDate(date.toDateString() || "")}
                                maxDate={productId != "free_trial" ? null : moment().add("1", "month").toLocaleString()}
                                placeholderText="Select Date"
                                className="w-[400px] px-4 py-3 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500"
                                style={{
                                    backgroundColor: colors.inputBackground,
                                    borderColor: colors.border,
                                    color: colors.text
                                }}
                                minDate={new Date()}
                                dateFormat="dd-MM-yyyy"
                            />
                        </div>
                        <div className="mb-4">
                            <label className="block text-sm font-medium mb-2" style={{ color: colors.text }}>Event Address</label>
                            <AddressAutocomplete
                                placeholder={"Enter Event Address"}
                                value={location}
                                onSelect={(val) => setLocation(val)}
                            />
                        </div>
                        <div className='text-center'>
                            <button
                                className="w-[200px] py-3 bg-blue-600 text-white rounded-full font-medium hover:bg-blue-700 transition mt-4">
                                Update
                            </button>
                        </div>
                    </form>
                </Modal>
                {/* Delete Event Modal */}
                <Modal isDarkMode={isDarkMode} key={"deleteform"} title="DELETE EVENT" isOpen={showDeleteModal} onClose={() => setShowDeleteModal(false)}>
                    <div className="text-center mb-6">
                        <p className="mb-6" style={{ color: colors.text }}>Are you sure you want to delete this event?</p>
                        <div className='text-center mt-5'>
                            <button
                                onClick={() => setShowDeleteModal(false)}
                                className="w-[200px] py-3 text-center font-medium hover:bg-gray-100 dark:hover:bg-gray-800 transition rounded-full"
                                style={{ color: colors.text }}>
                                Cancel
                            </button>
                            <button
                                onClick={handleDeleteEvent}
                                className="w-[200px] py-3 bg-blue-600 text-white rounded-full font-medium hover:bg-blue-700 transition mb-3">
                                Delete
                            </button>
                        </div>
                    </div>
                </Modal>
                {/* Upgrade Event Modal */}
                <Modal isDarkMode={isDarkMode} key={"upgradeform"} title="UPGRADE EVENT" isOpen={showUpgradeModal} onClose={() => { setSelectedPlan(null); setSelectedPlanId(''); setShowUpgradeModal(false) }}>
                    <div className="space-y-3">
                        <div className={`${isDarkMode ? "bg-gray-900 text-white" : "bg-white text-gray-900"} p-4 rounded-xl shadow-sm`}>
                            <h2 className="text-lg font-semibold">Current Plan</h2>
                            <p>You paid: <strong>${currentPlan?.price_value ?? "0.00"}</strong></p>
                            <p>Plan Name: {currentPlan?.description ?? "Free Trial"}</p>
                        </div>
                        <div className={`${isDarkMode ? "bg-gray-900 text-white" : "bg-white text-gray-900"} p-4 rounded-xl shadow-sm border`}>
                            <label className="block mb-2 font-medium">
                                Select New Plan:
                            </label>
                            {(currentPlan == null && subscriptionPackagesList) ?
                                <select
                                    value={selectedPlanId}
                                    onChange={e => setSelectedPlanId(e.target.value)}
                                    className={`${isDarkMode ? "bg-gray-900 text-white" : "bg-white text-gray-900"} w-full p-2 border rounded`}>
                                    <option value="">-- Choose a Upgraded Plan --</option>
                                    {subscriptionPackagesList && subscriptionPackagesList?.map((plan) => (
                                        <option key={plan.id} value={plan?.id}>
                                            {plan?.description} - ${plan?.price_value}
                                        </option>
                                    ))}
                                </select>
                                :
                                (currentPlan !== null && subscriptionPackagesList && (subscriptionPackagesList?.filter((spl) => spl?.price_value > currentPlan?.price_value).length == 0)) ?
                                    <label className="block mb-2 font-medium text-red-700">
                                        Sorry !!! You have already Subscribed a higher value package
                                    </label>
                                    :
                                    <select
                                        value={selectedPlanId}
                                        onChange={e => setSelectedPlanId(e.target.value)}
                                        className={`${isDarkMode ? "bg-gray-900 text-white" : "bg-white text-gray-900"} w-full p-2 border rounded`}>
                                        <option value="">-- Choose a Upgraded Plan --</option>
                                        {subscriptionPackagesList && subscriptionPackagesList?.filter((spl) => spl?.price_value > currentPlan?.price_value)?.map((plan) => (
                                            <option key={plan.id} value={plan?.id}>
                                                {plan?.description} - ${plan?.price_value}
                                            </option>
                                        ))}
                                    </select>
                            }
                        </div>
                        {selectedPlan && (
                            <>
                                <div className={`$${isDarkMode ? "bg-gray-900 text-white" : "bg-white text-gray-900"} p-4 rounded-xl border border-blue-300`}>
                                    <h3 className="font-semibold mb-1">Upgrade Summary:</h3>
                                    <p>New Plan Worth: <strong>${selectedPlan?.price_value}</strong></p>
                                    <p>Current Plan Value: <strong>${currentPlan?.price_value ?? 0}</strong></p>
                                    <p className="mt-2 text-blue-600 font-medium">
                                        After adjustment, you have to pay: <strong>${priceDifference?.toFixed(2)}</strong>
                                    </p>
                                </div>
                                <div className='text-right'>
                                    <button
                                        onClick={() => navigate(`/make-payment/${selectedEvent?._id}/${userData?._id}/internal/event/upgrade/${selectedPlan?.id}`)}
                                        className="w-[200px] py-3 bg-blue-600 text-white rounded-full font-medium hover:bg-blue-700 transition mt-2">
                                        Make Payment
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </Modal>
            </div>
            {isShowTour && <Tour
                open={tourOpen}
                onClose={() => setTourOpen(false)}
                onFinish={() => finishTour()}
                steps={steps}
                actionsRender={(originNode, { current, total }) => (
                    <>
                        {current == total - 1 && (
                            <label>
                                <input type='checkbox' value={showTourCheckbox} onChange={(e) => setShowTourCheckbox(e.target.checked)} /> &nbsp;
                                <span>Don't show this again</span>
                            </label>
                        )}
                        <br /><br />
                        {originNode}
                    </>
                )}
            />}
        </>
    );
};

export default EventsPage;